import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { storage } from '@/shared/storage/browserStorage';
import { STORAGE_KEYS } from '@/shared/config/constants';
import { logger } from '@/shared/observability/logger';
import { setSentryUser } from '@/shared/observability/sentry';
import type { SessionUser, SessionMembership } from '../types/auth.schema';

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(storage.getString(STORAGE_KEYS.authToken));
  const refreshToken = ref<string | null>(storage.getString(STORAGE_KEYS.refreshToken));
  const user = ref<SessionUser | null>(storage.getJson<SessionUser>(STORAGE_KEYS.user));
  const membership = ref<SessionMembership | null>(
    storage.getJson<SessionMembership>(STORAGE_KEYS.membership),
  );
  const pendingPhone = ref<string | null>(storage.getString(STORAGE_KEYS.pendingPhone));
  const isTenantAdmin = ref(false);

  const isAuthenticated = computed(() => Boolean(token.value && user.value));

  function setSession(payload: {
    token: string;
    refreshToken?: string | null;
    user: SessionUser;
    membership: SessionMembership;
  }) {
    token.value = payload.token;
    refreshToken.value = payload.refreshToken ?? null;
    user.value = payload.user;
    membership.value = payload.membership;

    storage.setString(STORAGE_KEYS.authToken, payload.token);
    if (payload.refreshToken) storage.setString(STORAGE_KEYS.refreshToken, payload.refreshToken);
    storage.setJson(STORAGE_KEYS.user, payload.user);
    storage.setJson(STORAGE_KEYS.membership, payload.membership);

    setSentryUser(payload.user);
    logger.info(
      { event: 'auth.login', userId: payload.user.id, membershipId: payload.membership.id },
      'session set',
    );
  }

  function setPendingPhone(phone: string | null) {
    pendingPhone.value = phone;
    if (phone) storage.setString(STORAGE_KEYS.pendingPhone, phone);
    else storage.remove(STORAGE_KEYS.pendingPhone);
  }

  function setTenantAdmin(value: boolean) {
    isTenantAdmin.value = value;
  }

  function clear() {
    const userId = user.value?.id;
    token.value = null;
    refreshToken.value = null;
    user.value = null;
    membership.value = null;
    isTenantAdmin.value = false;
    storage.clearNamespace('cc-admin:auth:');
    setSentryUser(null);
    logger.info({ event: 'auth.logout', userId }, 'session cleared');
  }

  return {
    token,
    refreshToken,
    user,
    membership,
    pendingPhone,
    isTenantAdmin,
    isAuthenticated,
    setSession,
    setPendingPhone,
    setTenantAdmin,
    clear,
  };
});
